/**
 * Coach Portal API Service
 * Coach-facing endpoints for viewing and responding to availability requests
 */

import apiClient from './client';
import type { CohortTimelineResponse } from './cohortEvents';
import type {
  AttendanceByModality,
  GroupCoachingSubType,
  SessionRsvpSummary,
  ModalityType,
  OneOnOneType,
  CalendlyBookingHealth,
  MissingMeetingLink,
} from '../../types';
import type { FeedbackSurveyQuestion } from './feedbackSurveys';
import type { BookingTouchpoint, BookingUnavailableReason } from './publicBooking';

// Coach feedback data from API (MED-330)
export interface CoachFeedbackData {
  config: {
    id: string;
    survey_type: string;
    schedule_status: string;
    survey_deadline: string | null;
    sent_at: string | null;
  } | null;
  questions: FeedbackSurveyQuestion[];
  submission: {
    id: string;
    coach_user_id: string;
    submitted_at: string;
    parsed_data: { responses: Record<string, unknown> };
  } | null;
}

// ============================================================================
// Types
// ============================================================================

// Interest status for coach response
export type InterestStatus = 'interested' | 'not_interested';

// Slot availability status
export type SlotAvailabilityStatus = 'full' | 'partial' | 'unavailable';

// Conflict date entry for partial availability
export interface SlotConflict {
  date: string; // ISO date string
  reason?: string;
}

// Per-slot availability data
export interface SlotAvailability {
  status: SlotAvailabilityStatus;
  conflicts?: SlotConflict[];
}

// Full slot availability object (keyed by slot ID)
export type SlotAvailabilityMap = Record<string, SlotAvailability>;

// Availability request for coach dashboard display
export interface CoachAvailabilityRequest {
  id: string;
  cohort_id: string;
  // MED-918 AC5/AC6: 'cancelled' added — set by the AC4 cascade when a CM
  // removes the coach from the cohort. The coach dashboard should treat
  // cancelled requests as terminal (no action needed by the coach).
  // MDF-233: 'superseded' added — the ask changed and a newer request replaced
  // this one. Distinct from 'cancelled' (the coach was removed from the
  // cohort). A superseded request stays visible to the coach as a greyed
  // "Replaced" card so they can see what changed, but it is not actionable.
  status: 'pending' | 'confirmed' | 'declined' | 'expired' | 'cancelled' | 'superseded';
  interest_status: InterestStatus | null;
  requested_at: string;
  response_deadline: string | null;
  responded_at: string | null;
  // Cohort details
  cohort_name: string;
  cohort_start_date: string | null;
  cohort_end_date: string | null;
  // Program details
  program_id: string | null;
  program_name: string | null;
  // Partner details
  partner_id: string | null;
  partner_name: string | null;
  // Request summary
  modalities: string[];
  requested_time_slots: string[];
  compensation_text: string | null;
  // Session info
  session_count: number;
  session_duration_minutes: number;
  // Computed
  requested_days: string[];
  groups_count: number;
  is_urgent: boolean;
  // MDF-233: a reminder reuses this request rather than creating a new one.
  last_reminded_at?: string | null;
  reminder_count?: number;
}

// Time slot for coach display
export interface TimeSlotForCoach {
  id: string;
  day_of_week: string;
  time: string;
  timezone: string;
  slot_number: number;
}

// Modality for coach display
export interface ModalityForCoach {
  id: string;
  modality_type: string;
  session_count: number | null;
  // MED-1025: per-modality duration, so mixed-modality cohorts render an
  // accurate per-modality breakdown instead of a single flattened duration.
  session_duration_minutes: number | null;
  configuration: Record<string, unknown>;
}

// Full availability request with cohort details for response modal
export interface AvailabilityRequestWithDetails {
  // Request fields
  id: string;
  cohort_id: string;
  coach_user_id: string;
  // MED-918 AC5/AC6: see CoachAvailabilityRequest above. When 'cancelled',
  // the magic-link response page renders a clear cancelled-state card
  // instead of the interactive form.
  // MDF-233: 'superseded' added — the ask changed and a newer request replaced
  // this one. Distinct from 'cancelled' (the coach was removed from the
  // cohort). A superseded request stays visible to the coach as a greyed
  // "Replaced" card so they can see what changed, but it is not actionable.
  status: 'pending' | 'confirmed' | 'declined' | 'expired' | 'cancelled' | 'superseded';
  interest_status: InterestStatus | null;
  requested_at: string;
  response_deadline: string | null;
  responded_at: string | null;
  modalities: string[];
  requested_time_slots: string[];
  compensation_text: string | null;
  cohort_context: string | null;
  slot_availability: SlotAvailabilityMap | null;
  max_groups: number | null;
  is_flexible_groups: boolean;
  additional_notes: string | null;
  // Cohort details
  cohort_name: string;
  cohort_start_date: string | null;
  cohort_end_date: string | null;
  cohort_description: string | null;
  participant_capacity: number | null;
  // Key dates for timeline
  coach_immersion_date: string | null;
  participant_orientation_date: string | null;
  cohort_wrap_up_date: string | null;
  // Program details
  program_id: string | null;
  program_name: string | null;
  // Partner details
  partner_id: string | null;
  partner_name: string | null;
  // Time slots
  time_slots: TimeSlotForCoach[];
  // Modality info
  modality_details: ModalityForCoach[];
  // Session info
  session_count: number;
  session_duration_minutes: number;
}

// Coach availability response payload
export interface CoachAvailabilityResponsePayload {
  interest_status: InterestStatus;
  slot_availability?: SlotAvailabilityMap;
  max_groups?: number;
  is_flexible_groups?: boolean;
  additional_notes?: string;
}

// Coach dashboard stats
export interface CoachDashboardStats {
  pending_requests: number;
  active_programs: number;
  upcoming_sessions: number;
  completed_this_month: number;
}

// Upcoming session for coach dashboard
// MED-781: includes participant_count for the Needs Attention rail (Starts Today / Prep Needed cards)
export interface CoachUpcomingSession {
  id: string;
  title: string;
  session_number: number | null;
  scheduled_start: string;
  scheduled_end: string;
  status: 'scheduled' | 'in_progress';
  group_id: string;
  group_name: string;
  cohort_id: string;
  cohort_name: string;
  participant_count: number;
  // MED-1106: server-resolved internal topic (full coalesce). Null when no
  // topic is set; composeCoachSessionLabel then composes "Session N: {group}".
  topic_name: string | null;
  // MED-1147: participant display-name override (MED-832), null when blank — the
  // value shown in the divergence tooltip.
  topic_display_name: string | null;
  // MED-1147: server-computed divergence boolean (AC1) — gates the
  // internal-awareness indicator.
  participant_display_differs: boolean;
  // MED-1181: the cohort's group-coaching per-modality LSD gate; false for a
  // classic MED01 cohort. Gates the dashboard "Starts Today" / "Prep Needed"
  // Needs-Attention cards built from this row.
  live_session_delivery_enabled: boolean;
  // MED-1350: a check-in carries a group_id, so the type tells the dashboard to
  // show it as a 1:1. participant_name is null for a group session. Optional
  // until every API serving this bundle returns them.
  session_type?: string;
  one_on_one_type?: OneOnOneType | null;
  participant_name?: string | null;
}

// MED-781: Most recently completed session for the "Session Report Ready"
// Needs Attention card. `null` from the endpoint when none exists.
export interface CoachRecentCompletedSession {
  id: string;
  title: string;
  session_number: number | null;
  scheduled_start: string;
  scheduled_end: string;
  attendance_marked_at: string | null;
  group_id: string;
  group_name: string;
  cohort_id: string;
  cohort_name: string;
  topic_name: string | null;
  // MED-1147: participant display-name override + divergence boolean for the
  // "Session Report Ready" card indicator.
  topic_display_name: string | null;
  participant_display_differs: boolean;
  // MED-1181: the cohort's group-coaching per-modality LSD gate; false for a
  // classic MED01 cohort. Gates the dashboard "Session Report Ready" card.
  live_session_delivery_enabled: boolean;
}

// Coach cohort (for My Cohorts list - E8-7)
export interface CoachCohort {
  id: string;
  name: string;
  code: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  partner_name: string | null;
  program_name: string | null;
  participant_count: number;
  coach_group_count: number;
  session_count: number;
  sessions_completed: number;
  contract_status: 'pending' | 'announced' | 'signed';
  // MED-1181: the cohort's group-coaching per-modality LSD gate; false for a
  // classic MED01 cohort. Gates LSD affordances derived from the My Cohorts list.
  live_session_delivery_enabled: boolean;
}

// ============================================================================
// Coach Cohort Detail Types (E8-8)
// ============================================================================

// Attendance summary for display
export interface AttendanceSummaryInfo {
  total: number;
  attended: number;
  partial: number;
  absent: number;
  pending: number;
  // MED-1266: null when no outcomes recorded (un-recorded, not 0%); coverage travels alongside.
  percentage: number | null;
  // MED-1266: marking coverage — marked (recorded outcomes) of population (marked + pending).
  marked: number;
  population: number;
}

// Session info for coach cohort detail view
export interface CoachSessionInfo {
  id: string;
  title: string;
  session_number: number | null;
  scheduled_start: string;
  scheduled_end: string;
  status: string;
  topic_name: string | null;
  // MED-1147: participant display-name override + divergence boolean for the
  // cohort-detail session-row indicator.
  topic_display_name: string | null;
  participant_display_differs: boolean;
  attendance_marked: boolean;
  attendance_summary: AttendanceSummaryInfo | null;
  feedback_submitted: boolean;
  open_concern_count: number;
  avg_engagement: number | null;
  finalized_at: string | null;
  rsvp_summary?: SessionRsvpSummary;
}

// Group info for coach cohort detail view
export interface CoachGroupInfo {
  id: string;
  name: string;
  participant_count: number;
  sessions: CoachSessionInfo[];
  time_slot: {
    day_of_week: string;
    time: string;
    timezone: string;
  } | null;
}

// Coach cohort detail response (E8-8)
export interface CoachCohortDetail {
  id: string;
  name: string;
  code: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  description: string | null;
  partner_id: string | null;
  partner_name: string | null;
  program_id: string | null;
  program_name: string | null;
  participant_count: number;
  session_count: number;
  // MED-1027: configured per-modality session counts (mirror the admin/CM
  // source). Drive the "N sessions each" labels in the coach Sessions tab
  // section headers — `one_on_one_session_count` for the 1:1 section and
  // `group_session_count` for the Group Coaching section. `session_count`
  // remains the cohort-wide aggregate (group + 1:1 + workshops).
  one_on_one_session_count: number;
  group_session_count: number;
  // MED-1350: the cohort's 1:1 length in minutes (modalities.session_duration_minutes),
  // or null. The Edit Session modal defaults a 1:1 end time from it. Older API
  // versions do not send it.
  one_on_one_session_duration_minutes?: number | null;
  // MED-1106 AC-B2/B3: the cohort's enabled modality types (same source of
  // truth as the CM Sessions tab). The Sessions tab gates its modality tabs to
  // these — Group Coaching / 1:1 / Workshops appear only when enabled.
  modalities: ModalityType[];
  sessions_completed: number;
  default_meeting_platform: string | null;
  default_meeting_link: string | null;
  coach_zoom_link: string | null;
  coach_teams_link: string | null;
  // MED-778: Feature toggle state
  coach_features_enabled: boolean;
  coach_google_meet_link: string | null;
  // MED-1181: the cohort's group-coaching per-modality LSD gate (single
  // cohort-level value — one group_coaching modality per cohort); false for a
  // classic MED01 cohort. Gates the cohort-detail "Prep Session" / "Start
  // Session" button, "PREP AVAILABLE" badge, and the by-session leader column.
  live_session_delivery_enabled: boolean;
  // MED-1319: true when the cohort has a group coaching or 1:1 modality
  // (MED-1353), so a closed cohort keeps the button and the window says why it
  // has no links. Older API versions do not send it.
  booking_links_available?: boolean;
  // MED-1350: true when the intro or midpoint check-in is on for the cohort.
  checkins_enabled: boolean;
  groups: CoachGroupInfo[];
}

// MED-1367: a current member of one of the coach's groups in a cohort.
// The API returns an explicit allow-list of fields; staff-only fields
// (internal notes, manager email, matching notes) never reach a coach.
export interface CoachCohortParticipant {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  email: string;
  role: string | null;
  business_unit: string | null;
  location: string | null;
  group_id: string;
  group_name: string;
  experience: string | null;
  primary_goal: string | null;
  sessions_attended: number;
  total_sessions: number;
  attendance_population: number;
  /** MED-1442: group coaching only, like the other legacy fields. */
  attendance_percentage: number | null;
  status: 'pending' | 'active' | 'withdrawn';
  withdrawn_at: string | null;
  attendance_by_modality?: AttendanceByModality;
  group_coaching_sub_type?: GroupCoachingSubType;
}

// MED-1367: a member row inside a coach's group.
export interface CoachCohortGroupMember {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  email: string;
  role: string | null;
}

// MED-1367: one of the coach's groups in a cohort, with the same
// aggregate stats the CM Groups view shows.
export interface CoachCohortGroup {
  id: string;
  name: string;
  member_count: number;
  attendance_percentage: number | null;
  attendance_marked: number;
  attendance_population: number;
  avg_engagement: number | null;
  members: CoachCohortGroupMember[];
}

// Cohort event visible to a coach (E8-14)
export interface CoachCohortEvent {
  id: string;
  event_type: string;
  event_name: string;
  event_number: number;
  scheduled_start: string | null;
  scheduled_end: string | null;
  timezone: string;
  video_link: string | null;
  status: string;
}

// ============================================================================
// Coach Resource Types (MED-237)
// ============================================================================

// Resource visible to a coach
export interface CoachResource {
  id: string;
  title: string;
  description: string | null;
  resource_type: 'file' | 'link';
  url: string | null;
  download_url?: string;
  original_filename: string | null;
  file_size: number | null;
  mime_type: string | null;
  category_name: string | null;
  is_evergreen: boolean;
  created_at: string;
}

// Paginated response for coach resources
export interface CoachResourcesResponse {
  data: CoachResource[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================================================
// Coach Session Feedback Types (E8-4, E8-5)
// ============================================================================

// Concern type enum
export type ConcernType =
  | 'low_engagement'
  | 'attendance_issues'
  | 'disruptive_behavior'
  | 'personal_crisis'
  | 'conflict_with_group_member'
  | 'technical_difficulties'
  | 'struggling_with_content'
  | 'not_completing_prework'
  | 'flight_risk'
  | 'other';

// Concern flag status
export type ConcernFlagStatus = 'open' | 'addressed';

// Coach session feedback record
// MED-888: partner-coach Q5 (`partner_group_engagement`) persists as numeric 1-5;
// all other values are strings. `Record<string, string | number>` covers both.
export interface CoachSessionFeedback {
  id: string;
  session_id: string;
  coach_id: string;
  responses: Record<string, string | number>;
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

// Coach session feedback with author details
export interface CoachSessionFeedbackWithAuthor extends CoachSessionFeedback {
  coach_name: string;
  coach_email: string;
}

// Concern flag with details
export interface ConcernFlagWithDetails {
  id: string;
  session_id: string;
  participant_id: string;
  coach_id: string;
  concern_type: ConcernType;
  notes: string | null;
  status: ConcernFlagStatus;
  resolution_notes: string | null;
  addressed_by: string | null;
  addressed_at: string | null;
  created_at: string;
  participant_name: string;
  participant_email: string;
  coach_name: string;
  session_title: string;
  session_number: number | null;
  session_date: string;
  addressed_by_name: string | null;
}

// Input for creating a concern flag
export interface CreateConcernFlagInput {
  participant_id: string;
  concern_type: ConcernType;
  notes?: string;
}

// ============================================================================
// Engagement Rating Types (MED-264)
// ============================================================================

// Input for creating engagement ratings
export interface EngagementRatingInput {
  participant_id: string;
  rating: number;
}

// Engagement rating with details (from server)
export interface ParticipantEngagementRating {
  id: string;
  session_id: string;
  participant_id: string;
  coach_id: string;
  rating: number;
  created_at: string;
  participant_name: string;
  participant_email: string;
  coach_name: string;
}

// Submit feedback request
// MED-888: same widening as `CoachSessionFeedback.responses` — values may be numeric
// for partner-coach answers (e.g., engagement scale).
export interface SubmitCoachFeedbackPayload {
  responses: Record<string, string | number>;
  concern_flags?: CreateConcernFlagInput[];
  engagement_ratings?: EngagementRatingInput[];
  // MED-867: true = Submit (flips sessions.feedback_submitted), false/omitted on
  // PUT = Save Draft. The post-session form sends this explicitly so a draft
  // leaves the session outstanding and the dashboard card persists (AC3 / IV3).
  submitted?: boolean;
}

// Coach feedback response (get own feedback)
export interface CoachFeedbackResponse {
  feedback: CoachSessionFeedback | null;
  concernFlags: ConcernFlagWithDetails[];
  engagementRatings: ParticipantEngagementRating[];
}

// ============================================================================
// Attendance Marking Types (E8-9, E8-10)
// ============================================================================

// Absent reason enum.
// MED-794 extended the DB enum with `technical_issues` and `other` to back the
// post-session screen's reason chip set (Personal / Technical issues / No-show
// / Other). `business_conflict` remains for legacy rows.
export type AbsentReason =
  | 'business_conflict'
  | 'personal_conflict'
  | 'no_notice'
  | 'technical_issues'
  | 'other';

// Attendance status for marking
export type AttendanceMarkingStatus = 'attended' | 'partial' | 'absent';

// Session needing attendance (for banner)
export interface SessionNeedingAttendance {
  id: string;
  title: string;
  session_number: number | null;
  scheduled_start: string;
  scheduled_end: string;
  // MED-1360: null for a 1:1 with no group (the API LEFT JOINs groups).
  group_id: string | null;
  group_name: string | null;
  cohort_id: string;
  cohort_name: string;
  // MED-1106: server-resolved internal topic (full coalesce); null when unset.
  // Feeds composeCoachSessionLabel for the Overdue Attendance + Submit Session
  // Feedback Needs Attention cards. Inherited by SessionNeedingFeedback.
  topic_name: string | null;
  // MED-1147: participant display-name override + divergence boolean for the
  // Overdue Attendance + Submit Session Feedback card indicators. Inherited by
  // SessionNeedingFeedback.
  topic_display_name: string | null;
  participant_display_differs: boolean;
}

// Pending attendance response
export interface PendingAttendanceResponse {
  count: number;
  sessions: SessionNeedingAttendance[];
}

// MED-867: a completed MED02 session whose Medley Feedback Form is unsubmitted.
// Same row shape as SessionNeedingAttendance (the card is a sibling of Overdue
// Attendance) plus the MED-1181 per-cohort LSD gate. The field lives ONLY on
// this feedback type — NOT on the base SessionNeedingAttendance — so the
// Overdue Attendance card stays correctly ungated (it is a pre-existing MED01
// feature per the ticket's "do not gate" list).
export interface SessionNeedingFeedback extends SessionNeedingAttendance {
  // MED-1181: false for a classic MED01 cohort; gates the post_session_feedback
  // Needs Attention card.
  live_session_delivery_enabled: boolean;
}

// MED-867: pending post-session feedback response.
export interface PendingFeedbackResponse {
  count: number;
  sessions: SessionNeedingFeedback[];
}

// Participant for attendance modal
export type RsvpStatus = 'pending' | 'accepted' | 'declined' | 'tentative';

export interface SessionParticipant {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  attendance_id: string | null;
  current_status: string;
  current_absent_reason: AbsentReason | null;
  current_notes: string | null;
  rsvp_status: RsvpStatus;
  participant_status?: string;
  withdrawn_at?: string | null;
}

// Session participants response
export interface SessionParticipantsResponse {
  session_id: string;
  session_title: string;
  session_number: number | null;
  session_date: string;
  group_id: string;
  group_name: string;
  participants: SessionParticipant[];
}

// Individual attendance record for submission
export interface ParticipantAttendanceInput {
  user_id: string;
  status: AttendanceMarkingStatus;
  absent_reason?: AbsentReason | null;
  notes?: string | null;
  // MED-979: per-participant free-text note authored by the coach on the
  // post-session "Attendance & Engagement" screen. Distinct from `notes`.
  coach_notes?: string | null;
}

// Mark attendance request
export interface MarkAttendanceRequest {
  attendance: ParticipantAttendanceInput[];
  send_surveys: boolean;
  engagement_ratings?: EngagementRatingInput[];
}

// Mark attendance response
export interface MarkAttendanceResponse {
  session_id: string;
  attendance_count: number;
  attended_count: number;
  partial_count: number;
  absent_count: number;
  attendance_percentage: number;
  surveys_triggered: boolean;
}

// Attendance summary
export interface AttendanceSummary {
  total: number;
  attended: number;
  partial: number;
  absent: number;
  pending: number;
  percentage: number;
}

// Session attendance record
export interface SessionAttendanceRecord {
  id: string;
  session_id: string;
  user_id: string;
  status: string;
  absent_reason: AbsentReason | null;
  notes: string | null;
  recorded_at: string | null;
  recorded_by: string | null;
  attendance_marked_at: string | null;
}

// Get attendance response
export interface GetAttendanceResponse {
  session_id: string;
  session_title: string;
  is_marked: boolean;
  summary: AttendanceSummary;
  attendees: SessionAttendanceRecord[];
}

// ============================================================================
// API Methods
// ============================================================================

export const coachPortalApi = {
  /**
   * Get dashboard statistics for the logged-in coach
   */
  getDashboardStats: async (): Promise<CoachDashboardStats> => {
    return apiClient.get<CoachDashboardStats>('/api/coach/dashboard/stats');
  },

  /**
   * Get compensation overview across all cohorts for the logged-in coach (E13-4).
   */
  getCompensation: async (): Promise<import('../../types').CoachCompensationOverview> => {
    return apiClient.get<import('../../types').CoachCompensationOverview>(
      '/api/coach/compensation',
    );
  },

  /**
   * Get the logged-in coach's own profile
   */
  getProfile: async (): Promise<import('../../types').Coach> => {
    return apiClient.get<import('../../types').Coach>('/api/coach/profile');
  },

  /**
   * Update the logged-in coach's own profile
   */
  updateProfile: async (data: import('../../types').UpdateCoachRequest): Promise<import('../../types').Coach> => {
    return apiClient.put<import('../../types').Coach>('/api/coach/profile', data);
  },

  /**
   * Upload the logged-in coach's own avatar
   */
  uploadAvatar: async (file: File): Promise<import('../../types').Coach> => {
    const formData = new FormData();
    formData.append('avatar', file);
    return apiClient.postFormData<import('../../types').Coach>('/api/coach/profile/avatar', formData);
  },

  /**
   * Get next 5 upcoming sessions for the logged-in coach (attendance not yet submitted)
   */
  getUpcomingSessions: async (): Promise<CoachUpcomingSession[]> => {
    return apiClient.get<CoachUpcomingSession[]>('/api/coach/sessions/upcoming');
  },

  /**
   * MED-781: Get the most recently completed session for the logged-in coach
   * (attendance marked AND scheduled_end in the past). Drives the "Session
   * Report Ready" Needs Attention card. Returns null when no completed session
   * exists.
   */
  getMostRecentCompletedSession: async (): Promise<CoachRecentCompletedSession | null> => {
    return apiClient.get<CoachRecentCompletedSession | null>(
      '/api/coach/sessions/most-recent-completed',
    );
  },

  /**
   * Get cohorts for the logged-in coach (E8-7)
   */
  getCohorts: async (): Promise<CoachCohort[]> => {
    return apiClient.get<CoachCohort[]>('/api/coach/cohorts');
  },

  /**
   * Get cohort detail for the logged-in coach (E8-8)
   */
  getCohortById: async (cohortId: string): Promise<CoachCohortDetail> => {
    return apiClient.get<CoachCohortDetail>(`/api/coach/cohorts/${cohortId}`);
  },

  /**
   * Get the current members of the coach's groups in a cohort (MED-1367).
   * Returns 404 when the coach leads no group in the cohort.
   */
  getCohortParticipants: async (cohortId: string): Promise<CoachCohortParticipant[]> => {
    return apiClient.get<CoachCohortParticipant[]>(
      `/api/coach/cohorts/${cohortId}/participants`,
    );
  },

  /**
   * Get the coach's groups in a cohort with members and stats (MED-1367).
   * Returns 404 when the coach leads no group in the cohort.
   */
  getCohortGroups: async (cohortId: string): Promise<CoachCohortGroup[]> => {
    return apiClient.get<CoachCohortGroup[]>(`/api/coach/cohorts/${cohortId}/groups`);
  },

  /**
   * Get cohort events the coach has been invited to (E8-14)
   */
  getCohortEvents: async (cohortId: string): Promise<CoachCohortEvent[]> => {
    return apiClient.get<CoachCohortEvent[]>(`/api/coach/cohorts/${cohortId}/events`);
  },

  /**
   * List availability requests for the logged-in coach
   */
  getAvailabilityRequests: async (): Promise<CoachAvailabilityRequest[]> => {
    return apiClient.get<CoachAvailabilityRequest[]>('/api/coach/availability-requests');
  },

  /**
   * Get a single availability request with full cohort details
   */
  getAvailabilityRequestById: async (requestId: string): Promise<AvailabilityRequestWithDetails> => {
    return apiClient.get<AvailabilityRequestWithDetails>(
      `/api/coach/availability-requests/${requestId}`,
    );
  },

  /**
   * Get availability request by magic link token (no auth required in theory,
   * but still uses the API client for consistency)
   */
  getAvailabilityRequestByToken: async (token: string): Promise<AvailabilityRequestWithDetails> => {
    return apiClient.get<AvailabilityRequestWithDetails>(
      `/api/coach/availability-requests/token/${token}`,
    );
  },

  /**
   * Submit availability response for a request
   */
  submitResponse: async (
    requestId: string,
    payload: CoachAvailabilityResponsePayload,
  ): Promise<AvailabilityRequestWithDetails> => {
    return apiClient.post<AvailabilityRequestWithDetails>(
      `/api/coach/availability-requests/${requestId}/respond`,
      payload,
    );
  },

  /**
   * Submit availability response via magic link token
   */
  submitResponseByToken: async (
    token: string,
    payload: CoachAvailabilityResponsePayload,
  ): Promise<AvailabilityRequestWithDetails> => {
    return apiClient.post<AvailabilityRequestWithDetails>(
      `/api/coach/availability-requests/token/${token}/respond`,
      payload,
    );
  },

  // ============================================================================
  // Coach Resources Methods (MED-237)
  // ============================================================================

  /**
   * Get resources for a cohort visible to the coach
   */
  getCohortResources: async (cohortId: string, search?: string): Promise<CoachResourcesResponse> => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return apiClient.getPaginated<CoachResource>(`/api/coach/cohorts/${cohortId}/resources${params}`);
  },

  // ============================================================================
  // Attendance Marking Methods (E8-9, E8-10)
  // ============================================================================

  /**
   * Get sessions needing attendance for the banner (E8-9)
   */
  getPendingAttendance: async (): Promise<PendingAttendanceResponse> => {
    return apiClient.get<PendingAttendanceResponse>('/api/coach/sessions/pending-attendance');
  },

  /**
   * MED-867: Get completed MED02 sessions whose Medley Feedback Form is still
   * unsubmitted. Drives the multi-item "Submit Session Feedback" Needs Attention
   * card. Backend route is gated by the `live_session_delivery` flag (404 when
   * off); callers also gate the fetch on the flag so it isn't called needlessly.
   */
  getPendingFeedback: async (): Promise<PendingFeedbackResponse> => {
    return apiClient.get<PendingFeedbackResponse>('/api/coach/sessions/pending-feedback');
  },

  /**
   * Get participants for a session (for attendance modal)
   */
  getSessionParticipants: async (sessionId: string): Promise<SessionParticipantsResponse> => {
    return apiClient.get<SessionParticipantsResponse>(
      `/api/coach/sessions/${sessionId}/participants`,
    );
  },

  /**
   * Get attendance records for a session
   */
  getAttendance: async (sessionId: string): Promise<GetAttendanceResponse> => {
    return apiClient.get<GetAttendanceResponse>(`/api/coach/sessions/${sessionId}/attendance`);
  },

  /**
   * Mark attendance for a session (E8-10)
   */
  markAttendance: async (
    sessionId: string,
    payload: MarkAttendanceRequest,
  ): Promise<MarkAttendanceResponse> => {
    return apiClient.post<MarkAttendanceResponse>(
      `/api/coach/sessions/${sessionId}/attendance`,
      payload,
    );
  },
  // ============================================================================
  // Session Feedback Methods (E8-4, E8-5)
  // ============================================================================

  /**
   * Get coach's own feedback for a session
   */
  getSessionFeedback: async (sessionId: string): Promise<CoachFeedbackResponse> => {
    return apiClient.get<CoachFeedbackResponse>(`/api/coach/sessions/${sessionId}/feedback`);
  },

  /**
   * Submit feedback and concern flags for a session
   */
  submitSessionFeedback: async (
    sessionId: string,
    payload: SubmitCoachFeedbackPayload,
  ): Promise<{ feedback: CoachSessionFeedback; flagsCreated: number }> => {
    return apiClient.post<{ feedback: CoachSessionFeedback; flagsCreated: number }>(
      `/api/coach/sessions/${sessionId}/feedback`,
      payload,
    );
  },

  /**
   * Update existing feedback and concern flags
   */
  updateSessionFeedback: async (
    sessionId: string,
    payload: SubmitCoachFeedbackPayload,
  ): Promise<{ feedback: CoachSessionFeedback; flagsCreated: number }> => {
    return apiClient.put<{ feedback: CoachSessionFeedback; flagsCreated: number }>(
      `/api/coach/sessions/${sessionId}/feedback`,
      payload,
    );
  },

  // ============================================================================
  // Session Notes Methods (MED-492)
  // ============================================================================

  /**
   * Get coach's personal note for a session
   */
  getSessionNotes: async (
    sessionId: string,
  ): Promise<{ content: string | null; session_id: string; updated_at?: string }> => {
    return apiClient.get<{ content: string | null; session_id: string; updated_at?: string }>(
      `/api/coach/sessions/${sessionId}/notes`,
    );
  },

  /**
   * Upsert coach's personal note for a session (auto-save)
   */
  upsertSessionNotes: async (
    sessionId: string,
    content: string,
  ): Promise<{ id: string; content: string; updated_at: string }> => {
    return apiClient.put<{ id: string; content: string; updated_at: string }>(
      `/api/coach/sessions/${sessionId}/notes`,
      { content },
    );
  },

  // ============================================================================
  // Huddle Methods (MED-268)
  // ============================================================================

  /**
   * Create a new huddle for a participant in a cohort
   */
  createHuddle: async (
    cohortId: string,
    data: import('../../types').CreateHuddleRequest,
  ): Promise<import('../../types').CoachHuddle> => {
    return apiClient.post<import('../../types').CoachHuddle>(
      `/api/coach/cohorts/${cohortId}/huddles`,
      data,
    );
  },

  /**
   * List huddles for a cohort (coach's groups only)
   */
  getHuddles: async (cohortId: string): Promise<import('../../types').CoachHuddle[]> => {
    const result = await apiClient.get<{
      data: import('../../types').CoachHuddle[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/api/coach/cohorts/${cohortId}/huddles`);
    return result.data;
  },

  /**
   * Get a single huddle by ID
   */
  getHuddleById: async (huddleId: string): Promise<import('../../types').CoachHuddle> => {
    return apiClient.get<import('../../types').CoachHuddle>(
      `/api/coach/huddles/${huddleId}`,
    );
  },

  /**
   * Update a huddle
   */
  updateHuddle: async (
    huddleId: string,
    data: import('../../types').UpdateHuddleRequest,
  ): Promise<import('../../types').CoachHuddle> => {
    return apiClient.put<import('../../types').CoachHuddle>(
      `/api/coach/huddles/${huddleId}`,
      data,
    );
  },

  /**
   * Reschedule a huddle
   */
  rescheduleHuddle: async (
    huddleId: string,
    data: import('../../types').RescheduleHuddleRequest,
  ): Promise<import('../../types').CoachHuddle> => {
    return apiClient.put<import('../../types').CoachHuddle>(
      `/api/coach/huddles/${huddleId}/reschedule`,
      data,
    );
  },

  /**
   * Cancel a huddle
   */
  cancelHuddle: async (huddleId: string): Promise<void> => {
    return apiClient.delete<void>(`/api/coach/huddles/${huddleId}`);
  },

  /**
   * Mark attendance for a huddle
   */
  markHuddleAttendance: async (
    huddleId: string,
    data: import('../../types').MarkHuddleAttendanceRequest,
  ): Promise<import('../../types').CoachHuddle> => {
    return apiClient.post<import('../../types').CoachHuddle>(
      `/api/coach/huddles/${huddleId}/attendance`,
      data,
    );
  },

  /**
   * Save coach feedback notes for a huddle
   */
  saveHuddleFeedback: async (
    huddleId: string,
    data: import('../../types').SaveHuddleFeedbackRequest,
  ): Promise<import('../../types').CoachHuddle> => {
    return apiClient.post<import('../../types').CoachHuddle>(
      `/api/coach/huddles/${huddleId}/feedback`,
      data,
    );
  },

  /**
   * Get cohort timeline for an availability request
   */
  getAvailabilityRequestTimeline: async (requestId: string): Promise<CohortTimelineResponse> => {
    return apiClient.get<CohortTimelineResponse>(
      `/api/coach/availability-requests/${requestId}/timeline`,
    );
  },
  // ============================================================================
  // Coach Milestone Feedback (MED-330)
  // ============================================================================

  /**
   * Get coach's own feedback data (config, questions, own submission)
   */
  getCoachFeedback: async (
    cohortId: string,
    surveyType: 'midpoint' | 'endpoint',
  ): Promise<CoachFeedbackData> => {
    return apiClient.get<CoachFeedbackData>(
      `/api/coach/cohorts/${cohortId}/feedback/${surveyType}`,
    );
  },

  /**
   * Submit coach feedback
   */
  submitCoachFeedback: async (
    cohortId: string,
    surveyType: 'midpoint' | 'endpoint',
    responses: Record<string, unknown>,
  ): Promise<{ id: string }> => {
    return apiClient.post<{ id: string }>(
      `/api/coach/cohorts/${cohortId}/feedback/${surveyType}`,
      { responses },
    );
  },

  // ============================================================================
  // Workshop Methods (MED-626)
  // ============================================================================

  /**
   * Get workshops assigned to the logged-in coach for a cohort
   */
  getWorkshops: async (cohortId: string): Promise<CoachWorkshop[]> => {
    return apiClient.get<CoachWorkshop[]>(`/api/coach/cohorts/${cohortId}/workshops`);
  },

  /**
   * Get participants for a workshop the coach facilitates
   */
  getWorkshopParticipants: async (cohortId: string, workshopId: string): Promise<CoachWorkshopParticipant[]> => {
    return apiClient.get<CoachWorkshopParticipant[]>(`/api/coach/cohorts/${cohortId}/workshops/${workshopId}/participants`);
  },

  // ============================================================================
  // Calendly Integration (E22-1)
  // ============================================================================

  /**
   * Get Calendly connection status
   */
  getCalendlyStatus: async (): Promise<CalendlyConnectionStatus> => {
    return apiClient.get<CalendlyConnectionStatus>('/api/calendly/status');
  },

  /**
   * Initiate Calendly OAuth flow — returns the authorization URL
   */
  connectCalendly: async (): Promise<{ authorization_url: string }> => {
    return apiClient.get<{ authorization_url: string }>('/api/calendly/connect');
  },

  /**
   * Complete Calendly OAuth callback
   */
  calendlyCallback: async (code: string, state: string): Promise<CalendlySettings> => {
    return apiClient.get<CalendlySettings>(
      `/api/calendly/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
    );
  },

  /**
   * Disconnect Calendly
   */
  disconnectCalendly: async (): Promise<void> => {
    return apiClient.post<void>('/api/calendly/disconnect');
  },

  /**
   * Get Calendly event types for mapping
   */
  getCalendlyEventTypes: async (): Promise<CalendlyEventTypeOption[]> => {
    return apiClient.get<CalendlyEventTypeOption[]>('/api/calendly/event-types');
  },

  /**
   * Update event type mappings (3 duration-based links: 20/45/60 min)
   */
  updateCalendlyMappings: async (data: {
    calendly_event_type_uri_20min?: string | null;
    calendly_event_type_uri_45min?: string | null;
    calendly_event_type_uri_60min?: string | null;
  }): Promise<CalendlySettings> => {
    return apiClient.put<CalendlySettings>('/api/calendly/event-type-mappings', data);
  },

  // ============================================================================
  // Booking links (MED-1319)
  // ============================================================================

  /**
   * Get the public booking links for a group, one for each touchpoint
   */
  getGroupBookingLinks: async (groupId: string): Promise<CoachBookingLinksResponse> => {
    return apiClient.get<CoachBookingLinksResponse>(`/api/coach/groups/${groupId}/booking-links`);
  },

  /**
   * Make a new token for a booking link. The old link stops working.
   */
  regenerateGroupBookingLink: async (
    groupId: string,
    linkId: string,
  ): Promise<{ link: CoachBookingLink }> => {
    return apiClient.post<{ link: CoachBookingLink }>(
      `/api/coach/groups/${groupId}/booking-links/${linkId}/regenerate`,
    );
  },
};

// ============================================================================
// Booking link Types (MED-1319)
// ============================================================================

export interface CoachBookingLink {
  id: string;
  touchpoint: BookingTouchpoint;
  touchpoint_label: string;
  duration_minutes: number | null;
  token: string;
  created_at: string;
  available: boolean;
  unavailable_reason: BookingUnavailableReason | null;
}

/** MED-1353: why a group has no booking links. */
export type BookingLinksEmptyReason =
  | 'cohort_closed'
  | 'group_inactive'
  | 'checkins_off'
  | 'no_bookable_modality';

export interface CoachBookingLinksResponse {
  calendly_connected: boolean;
  links: CoachBookingLink[];
  /** Null when links is not empty. Older API versions do not send it. */
  empty_reason?: BookingLinksEmptyReason | null;
}

// ============================================================================
// Calendly Types (E22-6)
// ============================================================================

export interface CalendlyConnectionStatus {
  connected: boolean;
  connected_at: string | null;
  event_types_configured: boolean;
  event_type_20min_uri: string | null;
  event_type_45min_uri: string | null;
  event_type_60min_uri: string | null;
  unmapped_durations: number[];
  /** MED-1363: null when not connected or when Calendly fails. */
  booking_health?: CalendlyBookingHealth | null;
  /** MED-1365 QA round 6: meeting links the coach still needs. */
  missing_meeting_links?: MissingMeetingLink[];
}

export interface CalendlySettings {
  calendly_user_uri: string | null;
  calendly_webhook_uri: string | null;
  calendly_event_type_uri_20min: string | null;
  calendly_event_type_uri_45min: string | null;
  calendly_event_type_uri_60min: string | null;
  calendly_connected_at: string | null;
}

export interface CalendlyEventTypeOption {
  uri: string;
  name: string;
  duration: number;
  kind: 'solo' | 'group';
  active: boolean;
  scheduling_url: string;
}

export interface CoachWorkshop {
  id: string;
  modality_id: string;
  title: string;
  description: string | null;
  sequence_number: number | null;
  participant_assignment: 'all_participants' | 'subset';
  facilitator_id: string | null;
  facilitator_name?: string;
  facilitator_email?: string;
  attendance_tracking_enabled: boolean;
  session_duration_minutes: number | null;
  created_at: string;
  updated_at: string;
  session_id?: string;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  zoom_link?: string | null;
  session_status?: string | null;
  attendance_marked?: boolean;
  feedback_submitted?: boolean;
  participant_count?: number;
}

export interface CoachWorkshopParticipant {
  attendance_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  rsvp_status: string | null;
  attendance_status: string | null;
  group_id?: string | null;
  group_name?: string | null;
}

export default coachPortalApi;
